import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
  useTheme,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaSearch, FaPlus, FaEdit, FaTrash, FaCopy } from "react-icons/fa";
import SupplierModal from "./SupplierModal";

const API = process.env.REACT_APP_API_URL;

// Short column headers for the run-membership grid — the full labels (with cron times)
// come from the crawler box and are shown as tooltips.
const RUN_SHORT = {
  dc: "DC",
  salesA: "Sales A",
  salesB: "Sales B",
  sdc: "SDC",
  sales: "Sales",
};

/**
 * Supplier registry for the selected retailer: who the crawler logs in as, with what
 * credentials, and which scheduled runs include them.
 *
 * This edits the JSON registry on the IONOS crawler box, which is the single source of
 * truth for BOTH the on-demand Crawl button and the scheduled cron crawlers — so a
 * supplier added here is picked up by the next scheduled run with no deploy.
 */
const SupplierRegistry = ({ crawlRetailer, retailerLabel, isCrawlable }) => {
  const toast = useToast();
  const theme = useTheme();
  const userid = localStorage.getItem("userid");

  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState(null); // record being edited (null = none)
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { isOpen: confirmOpen, onOpen: openConfirm, onClose: closeConfirm } = useDisclosure();
  const cancelRef = useRef(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const codeColor = useColorModeValue("gray.700", "gray.200");

  const load = useCallback(async () => {
    if (!isCrawlable) {
      setMeta(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(`${API}/edi-crawler/suppliers/detail`, {
        params: { userid, retailer: crawlRetailer },
      });
      setMeta(res.data?.data || null);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load the supplier registry";
      setLoadError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [userid, crawlRetailer, isCrawlable]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  const suppliers = useMemo(() => {
    const rows = meta?.suppliers || [];
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((s) => s.supplier.toLowerCase().includes(term) || (s.username || "").toLowerCase().includes(term));
  }, [meta, search]);

  const runKeys = meta?.runKeys || [];

  const copy = (value, label) => {
    navigator.clipboard?.writeText(value).then(
      () => toast({ status: "success", title: `${label} copied`, duration: 1500, isClosable: true }),
      () => toast({ status: "error", title: "Copy failed", duration: 2000, isClosable: true }),
    );
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/edi-crawler/suppliers/delete`, {
        userid,
        retailer: crawlRetailer,
        supplier: pendingDelete.supplier,
      });
      toast({
        status: "success",
        title: "Supplier removed",
        description: `${pendingDelete.supplier} will no longer be crawled for ${retailerLabel}.`,
        duration: 5000,
        isClosable: true,
      });
      closeConfirm();
      setPendingDelete(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to remove supplier";
      toast({ status: "error", title: "Delete failed", description: msg, duration: 6000, isClosable: true });
    } finally {
      setDeleting(false);
    }
  };

  if (!isCrawlable) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Supplier management is only available for <strong>Checkers</strong> and <strong>Dis-Chem</strong> (and
          MediRite/Petshop, which use the Checkers registry). Select one of those retailers in the top navigation.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      <Alert status="info" borderRadius="md" mb={4}>
        <AlertIcon />
        <AlertDescription fontSize="sm">
          These credentials live on the crawler server and drive <strong>both</strong> the on-demand Crawl button and the
          scheduled runs — a supplier added here is included in the next scheduled crawl, no deploy needed.
          {crawlRetailer === "CHK" && (
            <>
              {" "}
              New Checkers portal accounts must have their 8-digit OTP delivered to{" "}
              <strong>admin@spearmanagement.co.za</strong>, otherwise the crawler cannot complete the login — set that up
              on the Shoprite portal before the first run.
            </>
          )}
        </AlertDescription>
      </Alert>

      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
        <Box>
          <Text fontWeight="bold" fontSize="md">
            {retailerLabel} suppliers ({meta?.suppliers?.length || 0})
          </Text>
          <Text fontSize="xs" color={mutedColor}>
            Credentials the crawler signs in with, and which scheduled runs include each supplier.
          </Text>
        </Box>
        <Button
          size="sm"
          colorScheme="green"
          leftIcon={<FaPlus />}
          // Without `meta` there are no run keys to render, so adding would produce a
          // supplier assigned to nothing.
          isDisabled={!meta || isLoading}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          Add supplier
        </Button>
      </Flex>

      <InputGroup size="sm" mb={4} maxW="360px">
        <InputLeftElement pointerEvents="none">
          <FaSearch color={theme.colors.gray?.[400] || "#A0AEC0"} />
        </InputLeftElement>
        <Input placeholder="Search supplier or username..." value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="md" />
      </InputGroup>

      <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" boxShadow="sm" overflowX="auto">
        {isLoading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner />
          </Flex>
        ) : loadError ? (
          <Flex justify="center" align="center" py={10} px={4}>
            <Text fontSize="sm" color="red.400">
              {loadError}
            </Text>
          </Flex>
        ) : suppliers.length === 0 ? (
          <Flex justify="center" align="center" py={10}>
            <Text fontSize="sm" color={mutedColor}>
              {(meta?.suppliers?.length || 0) === 0 ? "No suppliers configured yet." : "No suppliers match your search."}
            </Text>
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Supplier</Th>
                <Th>Username</Th>
                <Th>Password</Th>
                {meta?.hasCountry && <Th>Country</Th>}
                {runKeys.map((key) => (
                  <Th key={key} textAlign="center">
                    <Tooltip label={meta?.runLabels?.[key] || key} placement="top">
                      <span>{RUN_SHORT[key] || key}</span>
                    </Tooltip>
                  </Th>
                ))}
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {suppliers.map((s) => (
                <Tr key={s.supplier} _hover={{ bg: rowHoverBg }} opacity={s.active ? 1 : 0.55}>
                  <Td fontWeight="medium">
                    {s.supplier}
                    {s.notes ? (
                      <Text fontSize="xs" color={mutedColor}>
                        {s.notes}
                      </Text>
                    ) : null}
                  </Td>
                  <Td>
                    <Flex align="center" gap={1}>
                      <Text fontSize="sm" fontFamily="mono" color={codeColor}>
                        {s.username}
                      </Text>
                      <IconButton aria-label="Copy username" icon={<FaCopy />} size="xs" variant="ghost" onClick={() => copy(s.username, "Username")} />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center" gap={1}>
                      <Text fontSize="sm" fontFamily="mono" color={codeColor}>
                        {s.password}
                      </Text>
                      <IconButton aria-label="Copy password" icon={<FaCopy />} size="xs" variant="ghost" onClick={() => copy(s.password, "Password")} />
                    </Flex>
                  </Td>
                  {meta?.hasCountry && (
                    <Td>
                      <Text fontSize="sm">{s.country}</Text>
                    </Td>
                  )}
                  {runKeys.map((key) => (
                    <Td key={key} textAlign="center">
                      {s.runs?.[key] ? (
                        <Badge colorScheme="green" borderRadius="md">
                          ✓
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color={mutedColor}>
                          —
                        </Text>
                      )}
                    </Td>
                  ))}
                  <Td>
                    <Badge colorScheme={s.active ? "green" : "gray"} borderRadius="md">
                      {s.active ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <IconButton
                      aria-label={`Edit ${s.supplier}`}
                      icon={<FaEdit />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      mr={1}
                      onClick={() => {
                        setEditing(s);
                        setModalOpen(true);
                      }}
                    />
                    <IconButton
                      aria-label={`Remove ${s.supplier}`}
                      icon={<FaTrash />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        setPendingDelete(s);
                        openConfirm();
                      }}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {meta?.configPath && (
        <Text fontSize="xs" color={mutedColor} mt={3}>
          Stored on the crawler server at <code>{meta.configPath}</code>. Every change is backed up and written to an
          audit log there.
        </Text>
      )}

      <SupplierModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        meta={meta}
        existing={editing}
        crawlRetailer={crawlRetailer}
        retailerLabel={retailerLabel}
      />

      <AlertDialog isOpen={confirmOpen} leastDestructiveRef={cancelRef} onClose={closeConfirm}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove {pendingDelete?.supplier}?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              This deletes the supplier and its stored portal credentials from the {retailerLabel} crawler registry. It
              will be dropped from every scheduled run and can no longer be crawled on demand. To pause a supplier
              instead — keeping the credentials — edit it and switch it to inactive.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeConfirm} variant="ghost">
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={deleting} loadingText="Removing...">
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SupplierRegistry;
